import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import PendingIcon from '@mui/icons-material/HourglassBottom';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RedeemIcon from '@mui/icons-material/Redeem';
import { adminFetch } from '../../lib/adminAuth';

/* ── Stat Card ─────────────────────────────────────────────── */
const StatCard = ({ label, value, icon, color }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #f0f0f0' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: color + '14', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>{value}</Typography>
        <Typography variant="body2" sx={{ color: '#999', mt: 0.3 }}>{label}</Typography>
      </Box>
    </Box>
  </Paper>
);

/* ── Simple Bar Chart ──────────────────────────────────────── */
const MiniChart = ({ data, color = '#4caf50', label }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #f0f0f0' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 2 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 100 }}>
        {data.length === 0 ? (
          <Typography variant="caption" sx={{ color: '#ccc' }}>No data yet</Typography>
        ) : data.map((d, i) => (
          <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{
              width: '100%', maxWidth: 20, borderRadius: '3px 3px 0 0', bgcolor: color,
              height: Math.max(3, (d.count / max) * 80),
              opacity: 0.7 + (d.count / max) * 0.3,
              transition: 'height 0.3s ease',
            }} />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption" sx={{ color: '#ccc', fontSize: 10 }}>
          {data.length > 0 ? data[0]._id?.slice(5) : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: '#ccc', fontSize: 10 }}>
          {data.length > 0 ? data[data.length - 1]._id?.slice(5) : ''}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block' }}>
        Last 30 days — Total: {data.reduce((s, d) => s + d.count, 0)}
      </Typography>
    </Paper>
  );
};

/* ── Top Vendors Table ─────────────────────────────────────── */
const TopVendors = ({ vendors }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #f0f0f0' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 2 }}>Top Vendors</Typography>
    {vendors.length === 0 ? (
      <Typography variant="caption" sx={{ color: '#ccc' }}>No activity yet</Typography>
    ) : (
      <Box>
        {vendors.map((v, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.2, borderBottom: i < vendors.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', bgcolor: '#fff8e7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 12, color: '#ffb71b',
              }}>
                {i + 1}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>{v.company}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{v.totalStamps}</Typography>
              <Typography variant="caption" sx={{ color: '#999' }}>stamps</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    )}
  </Paper>
);

/* ── Main Dashboard ────────────────────────────────────────── */
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [custRes, vendRes, analyticsRes] = await Promise.all([
          adminFetch('/api/admin/customers?limit=1'),
          adminFetch('/api/admin/vendors?limit=1'),
          adminFetch('/api/admin/analytics'),
        ]);
        const custData = await custRes.json();
        const vendData = await vendRes.json();
        const analyticsData = await analyticsRes.json();

        const allVendRes = await adminFetch('/api/admin/vendors?limit=999');
        const allVendData = await allVendRes.json();
        const pending = (allVendData.vendors || []).filter(v => !v.approved).length;

        setStats({
          customers: custData.total || 0,
          vendors: vendData.total || 0,
          pending,
        });
        setAnalytics(analyticsData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress size={28} sx={{ color: '#1a1a2e' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Dashboard</Typography>
      <Typography variant="body2" sx={{ color: '#999', mb: 4 }}>Overview of your Rewarding platform</Typography>

      {/* Stat cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 4 }}>
        <StatCard label="Customers" value={stats?.customers ?? '-'} icon={<PeopleIcon />} color="#4caf50" />
        <StatCard label="Vendors" value={stats?.vendors ?? '-'} icon={<StoreIcon />} color="#2196f3" />
        <StatCard label="Pending" value={stats?.pending ?? '-'} icon={<PendingIcon />} color="#ff9800" />
        <StatCard label="Rewards" value={analytics?.totals?.rewards ?? '-'} icon={<CardGiftcardIcon />} color="#9c27b0" />
        <StatCard label="Claimed" value={analytics?.totals?.claimed ?? '-'} icon={<RedeemIcon />} color="#e91e63" />
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 4 }}>
        <MiniChart data={analytics?.customerSignups || []} color="#4caf50" label="Customer Signups" />
        <MiniChart data={analytics?.vendorSignups || []} color="#2196f3" label="Vendor Signups" />
        <MiniChart data={analytics?.stampsOverTime || []} color="#ffb71b" label="Stamps Earned" />
        <MiniChart data={analytics?.claimsOverTime || []} color="#e91e63" label="Rewards Claimed" />
      </Box>

      {/* Top Vendors */}
      <Box sx={{ maxWidth: 500 }}>
        <TopVendors vendors={analytics?.topVendors || []} />
      </Box>
    </Box>
  );
}
